import React, { useEffect, useState } from 'react';

import { Flex, Modal, ModalBody, ModalContent, ModalFooter, ModalOverlay } from '@chakra-ui/react';
import { useTheme } from '@emotion/react';
import { useParsedQuery } from 'components/hooks/useParsedQuery';
import { useSelector } from 'react-redux';
import { REVIEW_RESULT_VIEW, useCurrentProvider, useSelectedReviewResultView } from 'reducers/reviewResultSlice';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import { isMobileView } from 'constants/isMobileView';

import { Button } from 'uiToolkitV1/Button';
import { Checkbox } from 'uiToolkitV1/Checkbox';
import { Paper } from 'uiToolkitV1/Paper';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { useReviewFormSidebarData, useReviewFormSidebarDataOld } from '../components/Sidebar/hooks/dataHooks';
import useReviewFormParams from '../hooks/useReviewFormParams';
import useReviewResultParams from '../hooks/useReviewResultParams';
import { useReviewAcknowledgeSignPoll, useReviewRequestAcknowledgeSign } from './dataHooks';

export const E_SIGN_PDF_STATUS = {
  STARTED: 'STARTED',
  COMPLETED: 'COMPLETED',
  ERROR: 'FAILED',
};

const SigningModalContentBody = () => {
  const { employeeId, reviewId, providerId } = useReviewFormParams();
  const selectedProviderData = useCurrentProvider();
  const { data: formData = {} } = useReviewFormSidebarDataOld({
    employeeId,
    providerId,
    reviewId,
  });

  const employeeEmail = formData?.employee?.metadata?.email;

  const {
    isLoading: requestInitiateDataIsLoading,
    isSuccess: requestInitiateDataSuccess,
    isError: requestInitiateDataError,
  } = useReviewRequestAcknowledgeSign({
    reviewId,
    providerId: selectedProviderData.id,
    responseId: selectedProviderData.reviewId,
    employeeId,
    employeeEmail,
  });

  const {
    data: pollData,

    isError: isPollError,
  } = useReviewAcknowledgeSignPoll({ feedbackFormId: reviewId, isEnabled: requestInitiateDataSuccess });

  const isGenerationComplete = pollData?.status === E_SIGN_PDF_STATUS.COMPLETED;

  const signingLink = pollData?.signingLink;

  if (requestInitiateDataError || isPollError) {
    return <TextBodyMedium>Report generation failed! Please try again later</TextBodyMedium>;
  }

  if (
    requestInitiateDataIsLoading ||
    (!requestInitiateDataSuccess && !requestInitiateDataError) ||
    (requestInitiateDataSuccess && !isGenerationComplete)
  ) {
    return <TextBodyMedium>Loading your performance review report...</TextBodyMedium>;
  }

  // if (requestInitiateDataSuccess && !isGenerationComplete) {
  //   return 'PDF generation in progress';
  // }

  if (isGenerationComplete) {
    return (
      <iframe
        title="e-sign-report"
        id="e-sign-report"
        style={{
          width: '100%',
          height: 'calc(100vh - 128px)',
          borderRadius: '8px',
          overflow: 'auto',
          boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.24)',
        }}
        src={signingLink}
      />
    );
  }
};

const SigningModal = ({ isOpen, onClose }) => {
  const theme = useTheme();

  return (
    <Modal isOpen={isOpen} isCentered size="5xl">
      <ModalOverlay />
      <ModalContent position="relative" gap={0} margin={isMobileView() ? '10px' : '0px'}>
        <ModalBody p="24px" color={theme.colors.text.g1}>
          {/* -------------------------------- */}
          <SigningModalContentBody />
          {/* -------------------------------- */}
        </ModalBody>
        <ModalFooter
          sx={{
            background: theme.colors.neutral.F5F7F9,
            borderTop: `1px solid ${theme.colors.border.CFD7DF}`,
            borderRadius: '0px 0px 8px 8px',
          }}
          p="15.5px 16px"
        >
          <Flex>
            <Button variant="outlined" onClick={onClose}>
              Finish
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const getAcknowledgementStatus = ({
  reviewInfo,
  isFormPublished,
  isManagerFormAcknowledged,
  loggedInEmployee,
  // selectedProvider,
  selectedView,
}) =>
  isFormPublished &&
  !isManagerFormAcknowledged &&
  reviewInfo.receiverEmployeeId == loggedInEmployee.id && // using == js type is different for some reason
  selectedView === REVIEW_RESULT_VIEW.QUESTION;

const RabbitResultDocusignAcknowledge = ({ setMockAcknowledgedOn }) => {
  const [isFinished, setIsFinished] = useState();

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  // const queryClient = useQueryClient();

  const [agreeConfirmation, setAgreeConfirmation] = useState(false);
  const [isAcknowledgeSignModalOpen, setIsAcknowledgeSignModalOpen] = useState(false);
  const { employeeId, reviewId, providerId } = useReviewResultParams();
  const { data: formData = {} } = useReviewFormSidebarData({ employeeId, providerId, reviewId });
  const { reviewInfo, isFormPublished, isManagerFormAcknowledged } = formData;
  const { autoNavigate = false } = useParsedQuery();
  const [isDoneAutoNavigation, setIsDoneAutoNavigation] = useState(false);
  const selectedView = useSelectedReviewResultView();

  // const selectedProvider = useCurrentProvider();

  const initiateSign = () => {
    setIsAcknowledgeSignModalOpen(true);
  };

  const showAcknowledgeFooter = getAcknowledgementStatus({
    reviewInfo,
    isFormPublished,
    isManagerFormAcknowledged,
    loggedInEmployee,
    // selectedProvider,
    selectedView,
  });

  useEffect(() => {
    if (!showAcknowledgeFooter) return;
    if (autoNavigate && !isDoneAutoNavigation) {
      const buttonElement = document.getElementsByClassName('review-form-action-button')[0];
      if (!buttonElement) {
        return;
      }
      setTimeout(() => {
        buttonElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 2000);
      setIsDoneAutoNavigation(true);
    }
  }, [showAcknowledgeFooter]);

  if (!showAcknowledgeFooter || isFinished) return null;

  const handleClose = () => {
    setIsAcknowledgeSignModalOpen(false);
    setIsFinished(true);
    const now = new Date();
    const nowIsoString = now.toISOString();
    setMockAcknowledgedOn(nowIsoString);
  };

  return (
    <>
      <Paper mt="16px" p="16px" boxShadow="0px 0px 2px rgba(0, 0, 0, 0.25)">
        <Flex justifyContent="space-between" bg="surface.white" flexDir={isMobileView() ? 'column' : 'row'} gap={1}>
          <Checkbox isChecked={agreeConfirmation} onChange={() => setAgreeConfirmation((val) => !val)}>
            By clicking &quot;Acknowledge&quot;, you will digitally sign and acknowledge receipt of your performance
            review and confirm understanding of your performance feedback
          </Checkbox>
          <Button disabled={!agreeConfirmation} px={6} onClick={initiateSign} className="review-form-action-button">
            Acknowledge
          </Button>
        </Flex>
      </Paper>
      <SigningModal isOpen={isAcknowledgeSignModalOpen} onClose={handleClose} />
    </>
  );
};

export default RabbitResultDocusignAcknowledge;
